import { createHttpHeader } from '@/utils/network/createHTTPHeaders';
import { decryptTreezorResponse } from '@/services/encryption/decryptTreezorResponse';
import { IEndUserKeys } from '@/store/models/encryption.model';
import { IPutUser, ISearchUser, IUser } from '@/features/user/store/user.model';
import { getStaticDashboardConfig } from '@/hooks/useDashboardConfig';
import { fetchQuery } from '@/utils/queryClient';
import { fetchMethods } from '@/types/FetchService.types';

export class UserService {
  static queryKey = 'Users';
  static searchUsers = async (filters?: ISearchUser, pageNumber?: number): Promise<IUser[]> => {
    try {
      const config = getStaticDashboardConfig();

      if (!filters) return Promise.reject('Error fetching user, no filters found');

      const headers = createHttpHeader();
      const baseUrl = `${config.api.baseUrl}/v1`;
      let usersUrl = `${baseUrl}/users`;

      if (filters.userId) usersUrl += `/${filters.userId}`;
      else {
        const params = new URLSearchParams();

        if (filters) {
          Object.keys(filters).forEach((key) => {
            if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
              if (typeof filters[key] === 'boolean') {
                params.set(key, filters[key] ? '1' : '0');
              } else {
                params.set(key, filters[key].toString().trim());
              }
            }
          });
        }
        if (pageNumber) params.set('pageNumber', pageNumber.toString());
        usersUrl += `?${params.toString()}`;
      }

      const usersResponse = await fetchQuery(
        [
          this.queryKey,
          {
            method: fetchMethods.GET,
            action: 'search',
            user: filters.userId || undefined,
            params: { ...filters, pageNumber },
          },
        ],
        usersUrl,
        { headers },
      );
      return await decryptTreezorResponse<IUser>(usersResponse, { isSequential: true });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static getUser = async (userId: number): Promise<IUser[]> => {
    try {
      const config = getStaticDashboardConfig();

      const headers = createHttpHeader();
      const baseUrl = `${config.api.baseUrl}/v1`;
      const userUrl = `${baseUrl}/users/${userId}`;

      const usersResponse = await fetchQuery(
        [
          this.queryKey,
          {
            method: fetchMethods.GET,
            action: 'search',
            user: userId || undefined,
            params: { userId },
          },
        ],
        userUrl,
        { headers },
      );
      return await decryptTreezorResponse<IUser>(usersResponse, { isSequential: true });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static updateUser = async (userRequest: IPutUser, userId: number): Promise<IUser> => {
    try {
      const config = getStaticDashboardConfig();
      userRequest = {
        ...userRequest,
        ...(userRequest.activityOutsideEu !== undefined && {
          activityOutsideEu: Number(userRequest.activityOutsideEu),
        }),
        ...(userRequest.economicSanctions !== undefined && {
          economicSanctions: Number(userRequest.economicSanctions),
        }),
        ...(userRequest.residentCountriesSanctions !== undefined && {
          residentCountriesSanctions: Number(userRequest.residentCountriesSanctions),
        }),
        ...(userRequest.involvedSanctions !== undefined && {
          involvedSanctions: Number(userRequest.involvedSanctions),
        }),
      };

      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.PUT, user: userId }],
        `${config.api.baseUrl}/v1/users/${userId}`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify(userRequest),
        },
      );

      const [user] = await decryptTreezorResponse<IUser>(response);
      return user;
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static sendForgotPassword = async (email: string): Promise<void> => {
    try {
      const config = getStaticDashboardConfig();

      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.POST, action: 'password forgot' }],
        `${config.api.baseUrl}/core-connect/password/forgot`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify({ email }),
        },
      );

      await decryptTreezorResponse(response);
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static deactivateUser = async (userId: number): Promise<IUser> => {
    try {
      const config = getStaticDashboardConfig();

      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.DELETE, user: userId }],
        `${config.api.baseUrl}/v1/users/${userId}?origin=OPERATOR`,
        {
          headers: createHttpHeader(),
        },
      );

      const [user] = await decryptTreezorResponse<IUser>(response);
      return user;
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static createUser = async (
    userRequest: Partial<IUser>,
    endUserKeys?: IEndUserKeys,
    batchIndex?: string,
  ): Promise<IUser> => {
    try {
      const config = getStaticDashboardConfig();

      const queryKey = batchIndex ? `${this.queryKey}-${batchIndex}` : this.queryKey;

      const userCreatedResponse = await fetchQuery(
        [queryKey, { method: fetchMethods.POST, params: { userRequest, endUserKeys } }],
        `${config.api.baseUrl}/v1/users`,
        {
          method: 'post',
          headers: createHttpHeader(),
          body: endUserKeys ? JSON.stringify({ ...endUserKeys, ...userRequest }) : JSON.stringify(userRequest),
        },
      );

      const [user] = await decryptTreezorResponse<IUser>(userCreatedResponse);
      return user;
    } catch (err) {
      return Promise.reject(err);
    }
  };
}
